import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useErrors } from '../../../../hooks/errors/use-errors';
import { useNative } from '../../../../hooks/native/use-native';
import { useHttpClient } from '../../../../utils/clients/rest-client';
import {
  CsvExportDownloadData,
  CsvExportDownloadError,
  CsvExportOrderData,
  CsvExportOrderError,
  MeterResetAggregateMeasurementsData,
  MeterResetAggregateMeasurementsError,
  MeterSetAmpTransformersData,
  MeterSetAmpTransformersError,
  MeterSetMeterEventingRulesData,
  MeterSetMeterEventingRulesError,
  MeterSetMeterPurposeData,
  MeterSetMeterPurposeError,
  MeterSetMeterTariffData,
  MeterSetMeterTariffError,
  ResetAggregateMeasurementsRequest,
  SetAmpTransformersRequest,
  SetMeterEventingRulesRequest,
  SetMeterPurposeRequest,
  SetMeterTariffRequest,
} from '../../types/openapi/openapi-types';

export const useResetAggregateMeasurements = () => {
  const { t } = useTranslation('backend-validation');
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();
  const { fireVibration } = useNative();

  return useMutation<
    AxiosResponse<MeterResetAggregateMeasurementsData>,
    AxiosError<MeterResetAggregateMeasurementsError>,
    ResetAggregateMeasurementsRequest
  >(
    ['optimizer-reset-aggregate-measurements'],
    async (body) => {
      fireVibration();
      return await httpClient.meter.meterResetAggregateMeasurements(body);
    },
    {
      onError: (error) => {
        handleRestErrors(error, t('DEVICE_SYNC_REQUEST_TIMEOUT'));
      },
    },
  );
};

export const useSetMeterAmpTransformers = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<
    AxiosResponse<MeterSetAmpTransformersData>,
    AxiosError<MeterSetAmpTransformersError>,
    SetAmpTransformersRequest
  >(['meter-setAmpTransformers'], async (body) => await httpClient.meter.meterSetAmpTransformers(body), {
    onError: (error) => {
      handleRestErrors(error);
    },
  });
};

export const useSetMeterPurpose = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<
    AxiosResponse<MeterSetMeterPurposeData>,
    AxiosError<MeterSetMeterPurposeError>,
    SetMeterPurposeRequest
  >(['meter-setMeterPurpose'], async (body) => await httpClient.meter.meterSetMeterPurpose(body), {
    onError: (error) => {
      handleRestErrors(error);
    },
  });
};

export const useMeterSetTariff = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<
    AxiosResponse<MeterSetMeterTariffData>,
    AxiosError<MeterSetMeterTariffError>,
    SetMeterTariffRequest
  >(['meter-setMeterTariff'], async (body) => await httpClient.meter.meterSetMeterTariff(body), {
    onError: (error) => {
      handleRestErrors(error);
    },
  });
};

export const useMeterSetEventingRules = () => {
  const httpClient = useHttpClient();

  return useMutation<
    AxiosResponse<MeterSetMeterEventingRulesData>,
    AxiosError<MeterSetMeterEventingRulesError>,
    SetMeterEventingRulesRequest
  >(['meter-setMeterEventingRules'], async (body) => await httpClient.meter.meterSetMeterEventingRules(body));
};

export const useMeterCsvExportOrder = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<AxiosResponse<CsvExportOrderData>, AxiosError<CsvExportOrderError>, string>(
    ['meter-csvExportOrder'],
    async (meterId) => await httpClient.csvExport.csvExportOrder(meterId),
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};

export const useMeterCsvExportDownload = () => {
  const httpClient = useHttpClient();
  const { handleRestErrors } = useErrors();

  return useMutation<AxiosResponse<CsvExportDownloadData>, AxiosError<CsvExportDownloadError>, string>(
    ['meter-csvExportDownload'],
    async (exportId) => await httpClient.csvExport.csvExportDownload(exportId),
    {
      onError: (error) => {
        handleRestErrors(error);
      },
    },
  );
};
